import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Form, 
  InputGroup,
  Modal
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { partnersAPI } from '../services/api';
import Icon from '../components/Icon';

// Import icons as components for proper TypeScript support
import * as Icons from 'react-icons/fa';

interface Partner {
  _id: string;
  name: string;
  nameAr: string;
  website: string;
  logo: { path: string, secure_url: string, public_id: string };
  description: string;
  createdAt: string;
}

const Partners: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);

  // Fetch partners on component mount
  useEffect(() => {
    fetchPartners();
  }, []);

  // Function to fetch partners from API
  const fetchPartners = async () => {
    setLoading(true);
    try {
      const response = await partnersAPI.getPartners();
      setPartners(response.data);
    } catch (err: any) {
      setError('Failed to fetch partners');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter partners based on search term
  const filteredPartners = partners.filter(partner => 
    (partner.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (partner.nameAr?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (partner.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Confirm partner deletion
  const confirmDelete = (partner: Partner) => {
    setPartnerToDelete(partner);
    setShowDeleteModal(true);
  };

  // Handle partner deletion
  const handleDelete = async () => {
    if (!partnerToDelete) return;
    
    try {
      await partnersAPI.deletePartner(partnerToDelete._id);
      setPartners(partners.filter(p => p._id !== partnerToDelete._id));
      setShowDeleteModal(false);
      setPartnerToDelete(null);
    } catch (err: any) {
      setError('Failed to delete partner');
      console.error(err);
    }
  };

  if (loading && partners.length === 0) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading partners...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="mb-0">Partners</h1>
        </Col>
        <Col xs="auto">
          <Link to="/partners/new">
            <Button variant="primary">
              <Icon icon={Icons.FaPlus} className="me-2" /> Add Partner
            </Button>
          </Link>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white py-3">
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">All Partners</h5>
            </Col>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Icon icon={Icons.FaSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search partners..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th style={{ width: '60px' }}>Logo</th>
                <th>Name</th>
                <th>Arabic Name</th>
                <th>Website</th>
                <th>Description</th>
                <th>Created At</th>
                <th style={{ width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPartners.length > 0 ? (
                filteredPartners.map((partner) => (
                  <tr key={partner._id}>
                    <td>
                      {partner.logo ? (
                        <img 
                          src={`${partner.logo.secure_url}`} 
                          alt={partner.name} 
                          style={{ width: '50px', height: '50px', objectFit: 'cover' }} 
                        />
                      ) : (
                        <div 
                          className="bg-light d-flex align-items-center justify-content-center" 
                          style={{ width: '50px', height: '50px' }}
                        >
                          <Icon icon={Icons.FaHandshake} />
                        </div>
                      )}
                    </td>
                    <td>{partner.name || 'Unnamed Partner'}</td>
                    <td>{partner.nameAr || 'Unnamed Partner'}</td>
                    <td>
                      {partner.website && (
                        <a href={partner.website} target="_blank" rel="noopener noreferrer">
                          {partner.website}
                        </a>
                      )}
                    </td>
                    <td>
                      {partner.description?.length > 50
                        ? `${partner.description.substring(0, 50)}...`
                        : partner.description || 'No description'}
                    </td>
                    <td>{formatDate(partner.createdAt)}</td>
                    <td>
                      <div className="d-flex">
                        <Link to={`/partners/edit/${partner._id}`} className="btn btn-sm btn-outline-primary me-2">
                          <Icon icon={Icons.FaEdit} />
                        </Link>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => confirmDelete(partner)}
                        >
                          <Icon icon={Icons.FaTrash} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-3">
                    {error ? error : 'No partners found'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the partner "{partnerToDelete?.name}"? 
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Partners; 